import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router';
import PropTypes from 'prop-types';
import {
  Typography, Button, Stack,
} from '@mui/material';

const NavItem = ({
  to = '',
  label = '',
  icon = null,
  isNativeHtmlLink = false,
  disabled = false,
}) => {
  const { pathname } = useLocation();
  const isActiveLink = useMemo(
    () => pathname === to
      || (pathname.includes('create') && to.includes('create'))
      || (pathname.includes('managereg') && to.includes('registration')),
    [pathname, to],
  );

  const linkColor = useMemo(() => {
    if (disabled) return 'grey.40';
    if (isActiveLink) return 'primary.50';
    return 'grey.80';
  }, [disabled, isActiveLink]);

  const iconWithColor = useMemo(() => {
    if (!icon) return null;
    // If it's already a React element, clone it with the color prop
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon, {
        sx: { ...icon.props.sx, color: 'inherit' },
      });
    }
    // If it's a component, render it with the color prop
    const IconComponent = icon;
    return <IconComponent sx={{ color: 'inherit' }} />;
  }, [icon]);

  return (
    <Button
      component={isNativeHtmlLink ? 'a' : Link}
      disableRipple
      disabled={disabled}
      to={isNativeHtmlLink ? null : to}
      href={isNativeHtmlLink ? to : null}
      sx={{
        width: '85px',
        color: linkColor,
        backgroundColor: isActiveLink ? 'primary.5' : 'transparent',
        height: '62px',
        borderRadius: '6px',
        '&:focus': {
          backgroundColor: 'rgba(74, 80, 221, 0.04)',
        },
        '&:visited': {
          color: 'grey.80',
        },
        '& svg': {
          width: '24px',
          height: '24px',
        },
      }}
    >
      <Stack alignItems="center" gap="4px">
        {iconWithColor}
        <Typography
          sx={{
            fontSize: '12px',
            textTransform: 'none',
            textAlign: 'center',
            color: 'inherit',
          }}
        >
          {label}
        </Typography>
      </Stack>
    </Button>
  );
};

NavItem.propTypes = {
  to: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.object,
  ]),
  isNativeHtmlLink: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default NavItem;
